import React, { useState } from 'react';
import { useOps } from '../context/OpsContext';
import { Calendar, Clock, Search, Filter, ChevronLeft, ChevronRight, User, MapPin } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import './Scheduling.css';

const Scheduling = () => {
    const { lessons = [], loading } = useOps();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB', {
            weekday: 'short', day: 'numeric', month: 'short'
        });
    };

    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleTimeString('en-GB', {
            hour: '2-digit', minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed': return 'completed';
            case 'scheduled': return 'scheduled';
            case 'cancelled': return 'cancelled';
            case 'no-show': return 'no-show';
            default: return 'gray';
        }
    };

    const filteredLessons = (lessons || []).filter(lesson => {
        const studentName = `${lesson.student_first_name || ''} ${lesson.student_last_name || ''}`.toLowerCase();
        const instructorName = `${lesson.instructor_first_name || ''} ${lesson.instructor_last_name || ''}`.toLowerCase();
        const matchesSearch = studentName.includes(searchTerm.toLowerCase()) ||
            instructorName.includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'All' || lesson.lesson_status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    if (loading) return <LoadingSpinner color="amber-500" message="Fetching Schedule..." />;

    return (
        <div className="page-container scheduling-page">
            <div className="page-header">
                <div>
                    <h2 className="page-title">Scheduling & Diary</h2>
                    <p className="page-subtitle">View and manage all scheduled driving lessons</p>
                </div>
            </div>

            <div className="filters-bar">
                <div className="search-input-wrapper">
                    <Search size={18} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search student or instructor..."
                        className="search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="flex gap-2">
                    <div className="search-input-wrapper" style={{ minWidth: '200px' }}>
                        <Filter size={18} className="search-icon" />
                        <select
                            className="search-input"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{ paddingLeft: '3rem' }}
                        >
                            <option value="All">All Statuses</option>
                            <option value="Scheduled">Scheduled</option>
                            <option value="Completed">Completed</option>
                            <option value="Cancelled">Cancelled</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="table-container card fade-in">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Date & Time</th>
                            <th>Student</th>
                            <th>Instructor</th>
                            <th>Type</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredLessons.length > 0 ? (
                            filteredLessons.map(lesson => (
                                <tr key={lesson.id}>
                                    <td>
                                        <div className="time-slot">
                                            <span className="date">
                                                <Calendar size={14} className="text-gold" />
                                                {formatDate(lesson.start_time)}
                                            </span>
                                            <span className="duration">
                                                {formatTime(lesson.start_time)} - {formatTime(lesson.end_time)}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-slate-50 text-slate-400 flex items-center justify-center border border-slate-100">
                                                <User size={16} />
                                            </div>
                                            <div>
                                                <div className="text-sm font-bold text-slate-800 uppercase tracking-tight">
                                                    {`${lesson.student_first_name || ''} ${lesson.student_last_name || ''}`.trim() || 'Missing Name'}
                                                </div>
                                                <div className="text-[10px] text-slate-400 font-bold">STUDENT</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="instructor-badge">
                                            <div className="instructor-avatar">
                                                {(lesson.instructor_first_name || 'I').charAt(0)}
                                            </div>
                                            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                                                {`${lesson.instructor_first_name || ''} ${lesson.instructor_last_name || ''}`.trim() || 'Unassigned'}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="badge badge-gray">{lesson.lesson_type || 'Practical'}</span>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${getStatusColor(lesson.lesson_status)}`}>
                                            {lesson.lesson_status || 'Scheduled'}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="text-center py-20 text-secondary italic">
                                    No lessons found for the selected criteria.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Scheduling;
